import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

const SIZES = [
  { v: 0.5, label: "500 KB" },
  { v: 3, label: "3 MB" },
  { v: 8, label: "8 MB" },
  { v: 400, label: "400 MB" },
];
const TIMES = [
  { v: 0.2, label: "200 ms" },
  { v: 30, label: "30 sec" },
  { v: 1800, label: "30 min" },
  { v: 5400, label: "90 min" },
];
const TRAFFIC = [
  { v: "steady", label: "constant, needs ms latency" },
  { v: "spiky", label: "rare / unpredictable" },
  { v: "window", label: "busy 2h daily window" },
  { v: "sched", label: "whole dataset on a schedule" },
];

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono-ui text-[10px] uppercase tracking-[0.12em] text-[#1C2430]/55">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="font-mono-ui text-[12.5px] bg-white border border-[var(--border-heavy)] px-3 py-2 focus:outline-none focus:border-[#1C2430] cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

export function EndpointPicker() {
  const [sz, setSz] = useState("0.5");
  const [tm, setTm] = useState("0.2");
  const [tf, setTf] = useState("steady");

  const { rows, pick, why } = useMemo(() => {
    const s = +sz, t = +tm, f = tf;
    const rows = [];
    const row = (name, dead, reason) => rows.push({ name, dead, reason });
    row("Real-time", s > 6 || t > 60, s > 6 ? "payload over 6 MB" : t > 60 ? "over the 60-second limit" : "fits: 6 MB / 60 s, always warm");
    row("Serverless", s > 4 || t > 60, s > 4 ? "payload over 4 MB" : t > 60 ? "over the 60-second limit" : "fits: 4 MB / 60 s, scales to zero");
    row("Async", t > 3600, t > 3600 ? "over the 60-minute limit" : "accepts up to 1 GB and 60 min, no idle cost");
    const batchDead = f !== "sched" && t <= 60 && s <= 6;
    row("Batch", batchDead, batchDead ? "works, but wasteful for small fast requests needing a response now" : "built for scoring whole datasets on a schedule");
    let pick, why;
    if (t > 3600) { pick = "Batch transform"; why = "90 minutes exceeds even async's 60-minute ceiling."; }
    else if (f === "sched") { pick = "Batch transform"; why = "\"whole dataset on a schedule\" is batch by definition, so no endpoint is needed."; }
    else if (s > 6 || t > 60) { pick = "Asynchronous endpoint"; why = "big payload and/or long processing, results delivered to S3."; }
    else if (f === "window") { pick = "Serverless + provisioned concurrency"; why = "a predictable daily window, so pre-warm for it and pay nothing the other 22 hours."; }
    else if (f === "spiky") { pick = "Serverless endpoint"; why = "small, infrequent requests, so it scales to zero between them."; }
    else { pick = "Real-time endpoint + auto scaling"; why = "small payload, constant traffic, millisecond latency."; }
    return { rows, pick, why };
  }, [sz, tm, tf]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#2F6F4F] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid="widget-endpoint-picker"
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-6">
        Try it · The endpoint picker — set the scenario, watch options die
      </div>
      <div className="flex flex-wrap gap-6 mb-6">
        <Select label="Request size" value={sz} onChange={setSz} options={SIZES} testid="ep-size" />
        <Select label="Processing time" value={tm} onChange={setTm} options={TIMES} testid="ep-time" />
        <Select label="Traffic" value={tf} onChange={setTf} options={TRAFFIC} testid="ep-traffic" />
      </div>
      <div className="space-y-1.5" data-testid="endpoint-output">
        {rows.map((r) => (
          <div key={r.name} className="text-[13.5px] flex items-baseline gap-2">
            <span className={`font-semibold ${r.dead ? "text-[#D26040] line-through decoration-[#D26040]/50" : "text-[#2F6F4F]"}`}>
              {r.dead ? "✗" : "✓"} {r.name}
            </span>
            <span className="text-[#1C2430]/65">— {r.reason}</span>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-[var(--border-light)] text-[14px]">
        <strong className="text-[#1C2430]">Answer: {pick}</strong>
        <span className="text-[#1C2430]/70"> — {why}</span>
      </div>
    </motion.div>
  );
}
